import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { LogIn, Mail, KeyRound } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Connexion réussie");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Échec de la connexion"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-72px)] grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1400&q=80"
          className="absolute inset-0 w-full h-full object-cover"
          alt="Bibliothèque Lafayette"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,27,46,0.85) 0%, rgba(10,27,46,0.35) 50%, rgba(10,27,46,0.6) 100%)" }}/>
        <div className="absolute bottom-12 left-10 right-10 text-white">
          <span className="overline" style={{ color: "var(--c-accent)" }}>{t("login.sideOverline")}</span>
          <h2 className="font-display text-4xl font-bold mt-2 leading-tight">{t("login.sideTitle1")} <span className="font-serif-italic">{t("login.sideTitle2")}</span></h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <span className="overline">{t("login.overline")}</span>
          <h1 className="font-display text-4xl font-bold mt-2">{t("login.title")}</h1>
          <p className="text-slate-600 mt-2 text-sm">{t("login.subtitle")}</p>

          <button data-testid="google-login-btn" onClick={googleLogin} className="mt-8 w-full btn-ghost justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 11v3.2h5.4c-.2 1.4-1.6 4.2-5.4 4.2-3.2 0-5.9-2.7-5.9-6s2.7-6 5.9-6c1.9 0 3.1.8 3.8 1.4l2.6-2.5C16.7 3.7 14.6 2.8 12 2.8 6.9 2.8 2.8 6.9 2.8 12s4.1 9.2 9.2 9.2c5.3 0 8.8-3.7 8.8-9 0-.6-.1-1.1-.2-1.5H12z"/></svg>
            {t("login.google")}
          </button>

          <div className="flex items-center gap-3 my-6 text-xs text-slate-400">
            <div className="hairline flex-1"/> {t("login.or")} <div className="hairline flex-1"/>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="field-label">{t("login.email")}</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="field-input pl-9" placeholder={t("login.emailPlaceholder")}/>
              </div>
            </div>
            <div>
              <label className="field-label">{t("login.password")}</label>
              <div className="relative">
                <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="field-input pl-9" placeholder="••••••••"/>
              </div>
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading} className="btn-primary w-full justify-center">
              <LogIn size={14}/> {loading ? t("login.submitting") : t("login.submit")}
            </button>
          </form>

          <div data-testid="login-invitation-info" className="mt-6 rounded-md border px-4 py-3 text-xs sm:text-sm leading-relaxed" style={{ background: "rgba(212,160,64,0.10)", borderColor: "rgba(212,160,64,0.40)", color: "var(--c-primary)" }}>
            {t("login.invitationInfo")}
          </div>
        </div>
      </div>
    </div>
  );
}
